import { Button, useTheme, Snackbar, Alert } from "@mui/material";
import React, { useState, useEffect } from "react";
import InputTextField from "../../../../components/InputTextField";
import FormikSubmitButton from "../../../../components/FormikSubmitButton";
import SearchableDropdown from "../../../../components/SearchableDropdown";
import { useNavigate } from "react-router";
import HeaderLayout from "../../../../components/HeaderLayout";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import api from "../../../../api/apiClient";
import PasswordRequirements from "../../../../components/PasswordRequirements";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  role: Yup.mixed().required("Role is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one capital letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain one special character"
    ),
  confirmpassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
});

export default function AddUser() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [toastOpen, setToastOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const navigateBack = () => navigate(-1);

  const initialValues = {
    name: "",
    email: "",
    role: "",
    password: "",
    confirmpassword: "",
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/role/dropDownRoles");
        if (res.data.success) {
          const roleOptions = res.data.data.map((r) => ({
            label: r.name,
            value: r.code, // or r.id depending on your design
          }));
          setRoles(roleOptions);
        } else {
          console.error("Failed to fetch roles:", res.data.message);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  const handleSubmit = async (
    values,
    { setSubmitting, setFieldError, resetForm }
  ) => {
    try {
      const payload = {
        UserName: values.name,
        EmailID: values.email,
        UserRole: values.role.value,
        Password: values.password,
      };

      const res = await api.post("/user", payload);

      if (!res.data.success) {
        throw new Error(res.data.message || "Failed to add user");
      }

      setToastOpen(true);
      setTimeout(() => {
        navigate("/user-list");
      }, 800);
    } catch (error) {
      console.error("Error adding user:", error);
      setFieldError("submit", error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen mr-1 ml-1">
      <div className="flex-shrink-0">
        <HeaderLayout name="Add User" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, isValid, dirty }) => (
          <Form className="flex flex-col flex-1">
            <div className="overflow-y-auto h-[62vh] px-5 py-6 space-y-10 hide-scrollbar">
              {errors.submit && (
                <div className="bg-red-100 text-red-700 p-3 rounded-md">
                  {errors.submit}
                </div>
              )}

              <div className="grid grid-cols-2 gap-[100px] !pl-3 !pr-3">
                <InputTextField
                  label="Name"
                  requiredMark
                  name="name"
                  placeholder="Enter Name"
                  type="text"
                />
                <InputTextField
                  label="Email Address"
                  requiredMark
                  name="email"
                  placeholder="Enter Email Address"
                  type="text"
                />
              </div>

              <div className="grid grid-cols-2 gap-[100px] !pl-3 !pr-3">
                <SearchableDropdown
                  label="User Role"
                  name="role"
                  required
                  placeholder={"Select Role"}
                  options={roles}
                  getOptionValue={(option) => option.value} // extract the code
                  className="w-full"
                />
                <div className="space-y-2">
                  <InputTextField
                    label="Password"
                    requiredMark
                    name="password"
                    placeholder="Enter Password"
                    type="password"
                    hiddenPassword
                  />

                  {/* ✅ Password Requirements */}
                  <PasswordRequirements name="password" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-[100px] !pl-3 !pr-3">
                <InputTextField
                  label="Confirm Password"
                  requiredMark
                  name="confirmpassword"
                  placeholder="Enter Confirm Password"
                  type="password"
                  hiddenPassword
                />
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center gap-4 justify-center pt-4 border-t bg-white">
              <Button
                onClick={navigateBack}
                className="w-[90px] h-[40px]"
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.buttonHover.main,
                  "&:hover": {
                    backgroundColor: theme.palette.buttonHover.secondary,
                  },
                  color: theme.palette.white.main,
                  textTransform: "none",
                }}
              >
                Back
              </Button>

              <FormikSubmitButton
                name={isSubmitting ? "Submitting..." : "Submit"}
                disabled={isSubmitting || !isValid || !dirty}
                className="w-[90px] h-[40px]"
              />
            </div>
          </Form>
        )}
      </Formik>

      {/* Success Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          User successfully created!
        </Alert>
      </Snackbar>
    </div>
  );
}
